import { File } from "expo-file-system";
import type { PlayerAudioSource } from "./source.types";
import type { DataSession } from "../../../data/core/session";
import { SessionEndedError } from "../../../data/core/sessionLink";
import { OfflineAccessInvalidError } from "@utils/offlineAccess/errors";
import { logger } from "@utils/logger";

export function isOfflineAudioUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return url.startsWith("file:") || url.includes("/downloads/");
}

function getSourceType(
  url: string,
  isOffline: boolean,
): PlayerAudioSource["sourceType"] {
  if (isOffline) return "offline";
  return url.includes(".m3u8") ? "hls" : "remote";
}

export function resolveAudioSourceSyncIfPossible(
  data: DataSession,
  params: {
    urlOnline: string | null | undefined;
  },
): PlayerAudioSource | null {
  const canonicalUrl = params.urlOnline ?? "";
  const isOffline = isOfflineAudioUrl(canonicalUrl);
  if (isOffline) {
    if (!data.offlineAccess.observeOfflineAccess()) return null;

    // Synchronously check if file exists using new File API
    const file = new File(canonicalUrl);
    if (!file.exists) {
      console.audio.warn("Local file missing, needs fresh URL:", canonicalUrl);
      return null; // Signal caller to fetch fresh data
    }
    return {
      canonicalUrl,
      resolvedUrl: canonicalUrl,
      isOffline: true,
      sourceType: "offline",
    };
  }

  // Keep dependency loading identical to async version (still sync, just lazy).
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { signUrlSyncIfPossible } = require("@utils/index");

  const resolvedUrl = signUrlSyncIfPossible(data, canonicalUrl);
  if (typeof resolvedUrl !== "string") return null;

  return {
    canonicalUrl,
    resolvedUrl,
    isOffline: false,
    sourceType: getSourceType(canonicalUrl, false),
  };
}

export async function resolveAudioSource(
  data: DataSession,
  params: {
    urlOnline: string | null | undefined;
    forceRefreshCDNKey?: boolean;
  },
): Promise<PlayerAudioSource> {
  const canonicalUrl = params.urlOnline ?? "";
  const isOffline = isOfflineAudioUrl(canonicalUrl);

  if (isOffline) {
    let access = data.offlineAccess.observeOfflineAccessState();
    if (access.reason === "auth_pending") {
      if (!(await data.offlineAccess.waitForUser()))
        throw new SessionEndedError();
      access = data.offlineAccess.observeOfflineAccessState();
    }
    if (!access.isValid) {
      // The caller falls back to streaming; the sheet is the playback slice's.
      logger.warn("player", "player.offline_playback_refused", {
        reason: access.reason,
        canonicalUrl,
        outcome: "stream",
      });
      throw new OfflineAccessInvalidError(canonicalUrl, access.reason);
    }

    const file = new File(canonicalUrl);
    if (!file.exists) {
      console.audio.warn("Local file missing, needs fresh URL:", canonicalUrl);
      throw new Error(`MISSING_LOCAL_FILE:${canonicalUrl}`);
    }
    console.audio.log("DEBUG resolved url online:", canonicalUrl);
    return {
      canonicalUrl,
      resolvedUrl: canonicalUrl,
      isOffline: true,
      sourceType: "offline",
    };
  }

  // Lazily require to keep Jest/environment imports lightweight.
  const { signUrl, signUrlSyncIfPossible } = require("@utils/index");

  const resolvedUrlSync = params.forceRefreshCDNKey
    ? null
    : signUrlSyncIfPossible(data, canonicalUrl);
  const resolvedUrl =
    typeof resolvedUrlSync === "string"
      ? resolvedUrlSync
      : params.forceRefreshCDNKey
        ? await signUrl(data, canonicalUrl, { forceRefreshCDNKey: true })
        : await signUrl(data, canonicalUrl);

  console.audio.log("DEBUG resolved url offline:", resolvedUrl);
  return {
    canonicalUrl,
    resolvedUrl,
    isOffline: false,
    sourceType: getSourceType(canonicalUrl, false),
    cdnKeyRefreshRequested: params.forceRefreshCDNKey,
  };
}
